// The "Organizations & access policies" tour — explains the multitenancy
// layer: organizations that own projects, member roles, the customer
// policy-API integration, and the per-data-source access policies that
// filter what each user sees inside apps. Global tour (offered in every
// project, after the core product tour). Note: the Organizations page is
// only rendered for org/platform admins — the audience of this tour.

export const organizationsTour = {
  key: "organizations-policies",
  title: "Organizations & access policies",
  description:
    "How cortex isolates tenants: organizations own projects, members carry roles, and per-source access policies decide which rows and columns every user sees — including inside apps.",
  steps: [
    {
      title: "One platform, many organizations",
      body:
        "Cortex is <b>multitenant</b>: every project belongs to exactly one <b>organization</b>, " +
        "and users only see the projects (and apps, and data) their organization owns.<br/><br/>" +
        "Access is enforced server-side at every door — the API, the assistant/MCP surface, the " +
        "embedded Flow studio, and app embeds — not just hidden in the UI.<br/><br/>" +
        "This tour shows where organizations are managed and how <b>data-level access policies</b> " +
        "control what each user sees.",
    },
    {
      page: "organizations",
      target: '[data-tour="nav-organizations"], main',
      title: "The Organizations manager",
      body:
        "Organizations live here in the sidebar — visible only to <b>admins</b>. " +
        "A <b>platform admin</b> (the operator) sees every organization; an <b>org admin</b> " +
        "sees and manages only their own.",
    },
    {
      target: '[data-tour="page-organizations"], main',
      title: "Organizations own everything",
      body:
        "The left column lists organizations; platform admins create new ones with " +
        "<b>New Organization</b>. Selecting one shows its members and projects.<br/><br/>" +
        "An organization can be <b>renamed</b>, and deleted once it owns no projects — " +
        "guard rails prevent orphaning data.",
    },
    {
      demo: "new-org",
      target: '[data-testid="org-slug"], [data-tutorial-demo-modal]',
      title: "Creating an organization",
      body:
        "This is the actual create form (shown with sample values — nothing is saved during the " +
        "tour). Two fields: a display <b>name</b> and a stable <b>slug</b>. The slug is the " +
        "tenancy anchor: projects reference it as their owner, and every permission check " +
        "resolves through it — so it can't be changed casually later. Pick it like a hostname.",
    },
    {
      target: '[data-tour="org-members"], main',
      title: "Members & roles",
      body:
        "Each member has a <b>role</b>: <b>org_admin</b> manages the organization and its " +
        "projects; <b>member</b> works inside them; <b>app_user</b> can only open published " +
        "apps (never this console). <b>platform_admin</b> is cross-organization.",
    },
    {
      demo: "add-member",
      target: '[data-testid="member-role"], [data-tutorial-demo-modal]',
      title: "Adding a member",
      body:
        "The real add-member form (sample values, display only). Enter an <b>email</b> — if the " +
        "account already exists it just gets a membership; add a <b>password</b> and the account " +
        "is created in the same step. That's the whole invite flow.<br/><br/>" +
        "The <b>role</b> dropdown you see spotlighted decides what the member can do; adding an " +
        "email that's already a member simply updates its role.",
    },
    {
      demo: "policy-integration",
      target: '[data-testid="provider-url"], [data-tutorial-demo-modal]',
      title: "Bring the customer's existing policies",
      body:
        "The <b>Policy integration</b> form (sample values, display only) connects an org to the " +
        "customer's <b>existing entitlement / RBAC API</b>. The endpoint URL is templated — " +
        "<code>{email}</code> is substituted per user — and the returned JSON merges into the " +
        "user's <b>subject attributes</b>, so data policies can reference entitlements that live " +
        "in the customer's own system. Responses are cached (TTL below), and a provider failure " +
        "never widens access — unresolvable attributes <b>fail closed</b>.",
    },
    {
      target: '[data-tour="org-projects"], main',
      title: "Project ownership",
      body:
        "The projects an organization owns. Ownership is the tenancy anchor: it decides who can " +
        "enter the project, run its pipeline, talk to its agents and open its apps. Platform " +
        "admins can <b>move</b> a project to another organization from here.",
    },
    {
      page: "data-sources",
      target: '[data-tour="page-data-sources"], main',
      title: "Access policies live on the data",
      body:
        "Row- and column-level rules are configured per <b>data source</b> — open any source and " +
        "find its <b>Access Policies</b> panel.",
    },
    {
      page: "data-sources",
      demo: "add-policy",
      target: '[data-testid="policy-definition"], [data-tutorial-demo-modal]',
      title: "Writing a policy",
      body:
        "The real policy form (sample values, display only). A <b>row filter</b> is a SQL " +
        "condition over the data's columns with per-user placeholders — the spotlighted example, " +
        "<code>region = :subject.region</code>, means each user only sees rows matching their own " +
        "region attribute. Switch the kind to <b>column mask</b> to hide sensitive columns " +
        "(<code>ssn</code>, <code>salary</code>…) unless the user carries an exempting attribute." +
        "<br/><br/>Missing attributes always <b>deny</b> — a policy can fail closed, never open.",
    },
    {
      title: "Policies follow the data into apps",
      body:
        "Policies are part of the governed recipe — <b>versioned in Dolt</b>, every change an " +
        "auditable commit — and they are applied when data is read on a user's behalf: dashboards, " +
        "published apps, and analyst queries all pass through the same filter.<br/><br/>" +
        "Admins can dry-run any user's effective access with <b>Preview as user</b> on the panel. " +
        "Platform admins bypass data policies; everyone else is subject to them — and anything " +
        "unresolvable denies rather than leaks. That's the whole model: organizations wall off " +
        "projects, roles gate the console, and data policies decide what every user actually sees.",
    },
  ],
};
